import { useState, type ReactNode } from 'react';
import {
  ArrowFatUpIcon,
  BackspaceIcon,
  CaretDownIcon,
} from '@phosphor-icons/react';

// Touch on-screen keyboard for the kiosk. The kiosk runs Chromium under Wayland
// (labwc) with no OS virtual keyboard, so focusing a text field shows a caret
// but no keys — we render our own. Shared across kiosk text inputs (music
// search, AnyList add/rename). The host owns the input value; this component
// just emits edits:
//   - onKey(ch)   a character was typed (append at the caret/end)
//   - onBackspace delete the last character
//   - onClose     hide the keyboard (the ⌄ key)
//   - onEnter     optional submit (e.g. AnyList "add"); shows an Enter key
//
// Key presses use onPointerDown+preventDefault so tapping a key never steals
// focus from (or blurs) the host input — the caret stays put while typing (and
// the host's onBlur, which hides the keyboard, only fires on a real tap-away).

const ROWS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
];

// Symbols layer, toggled by the ?123 key in the bottom row. The digits row
// stays put (it's always visible in both layers); only the three letter rows
// swap out. Row lengths mirror the letter rows (10/9/7) so key widths line
// up between layers instead of reflowing.
const SYMBOL_ROWS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['-', '/', ':', ';', '(', ')', '$', '&', '@', '"'],
  ['.', ',', '?', '!', "'", '#', '%', '+', '='],
  ['*', '_', '\\', '|', '~', '<', '>'],
];

export interface OnScreenKeyboardProps {
  onKey: (ch: string) => void;
  onBackspace: () => void;
  onClose: () => void;
  /** When provided, an Enter key is shown that calls this (e.g. submit a list item). */
  onEnter?: () => void;
  /** Label for the Enter key (default "Enter"). */
  enterLabel?: string;
}

function Key({
  onTap,
  children,
  className,
  ariaLabel,
  active,
}: {
  onTap: () => void;
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      // Keep the host input focused (caret intact) — don't let the tap blur it.
      onPointerDown={(e) => e.preventDefault()}
      onClick={onTap}
      className={`h-20 grid place-items-center rounded-xl border text-2xl font-semibold transition-transform active:scale-[0.95] ${
        active
          ? 'bg-fg text-canvas border-transparent'
          : 'bg-elevated text-fg border-border'
      } ${className ?? 'flex-1'}`}
    >
      {children}
    </button>
  );
}

// Shift state: off → on (one-shot, capitalizes the next letter) → lock (caps
// lock) → off, cycled by tapping the shift key.
type Shift = 'off' | 'on' | 'lock';

export function OnScreenKeyboard({
  onKey,
  onBackspace,
  onClose,
  onEnter,
  enterLabel = 'Enter',
}: OnScreenKeyboardProps) {
  // Default to one-shot shift so the first letter of the first word the
  // user types lands capitalized (sentence-case affordance — most inputs
  // the keyboard fronts are names/labels, not all-lowercase tokens).
  // Reverts to lowercase automatically after the first letter via the
  // existing one-shot logic in pressLetter().
  const [shift, setShift] = useState<Shift>('on');
  // Letters ↔ symbols layer toggle (?123 / ABC key in the bottom row).
  const [symbols, setSymbols] = useState(false);
  const upper = shift !== 'off';
  const rows = symbols ? SYMBOL_ROWS : ROWS;

  // Type a letter, applying shift; one-shot shift reverts after one letter.
  const pressLetter = (ch: string) => {
    onKey(upper ? ch.toUpperCase() : ch);
    if (shift === 'on') setShift('off');
  };
  const cycleShift = () =>
    setShift((s) => (s === 'off' ? 'on' : s === 'on' ? 'lock' : 'off'));

  return (
    <div className="relative shrink-0 flex flex-col gap-2 border-t border-border pt-3 pb-12 select-none">
      {/* Always-on soft fade above the keyboard, so scrollable content
          dissolves into it rather than ending in a hard edge (independent of
          scroll position). */}
      <div className="pointer-events-none absolute inset-x-0 bottom-full h-16 bg-gradient-to-t from-canvas to-transparent" />
      {rows.map((row, ri) => {
        // Only letter keys respond to shift; digits (row 0) and the whole
        // symbols layer type as-is.
        const isLetters = !symbols && ri > 0;
        const isLast = ri === rows.length - 1;
        return (
          <div key={ri} className="flex gap-2 justify-center">
            {/* Shift / caps lock at the left of the last letter row. Hidden
                in the symbols layer, where shift has nothing to act on. */}
            {isLast && !symbols && (
              <Key
                onTap={cycleShift}
                ariaLabel={shift === 'lock' ? 'Caps lock on' : 'Shift'}
                active={upper}
                className="flex-[1.6]"
              >
                <ArrowFatUpIcon
                  size={32}
                  weight={shift === 'lock' ? 'fill' : 'bold'}
                />
              </Key>
            )}
            {row.map((ch) => (
              <Key
                key={ch}
                onTap={() => (isLetters ? pressLetter(ch) : onKey(ch))}
              >
                {isLetters && upper ? ch.toUpperCase() : ch}
              </Key>
            ))}
            {/* Backspace at the end of the last letter row. */}
            {isLast && (
              <Key onTap={onBackspace} ariaLabel="Backspace" className="flex-[1.6]">
                <BackspaceIcon size={32} weight="bold" />
              </Key>
            )}
          </div>
        );
      })}
      <div className="flex gap-2">
        <Key onTap={onClose} ariaLabel="Hide keyboard" className="flex-[1.6]">
          <CaretDownIcon size={32} weight="bold" />
        </Key>
        <Key
          onTap={() => setSymbols((s) => !s)}
          ariaLabel={symbols ? 'Show letters' : 'Show symbols'}
          className="flex-[1.6] !text-xl"
        >
          {symbols ? 'ABC' : '?123'}
        </Key>
        <Key onTap={() => onKey(' ')} ariaLabel="Space" className="flex-[6]">
          {' '}
        </Key>
        {onEnter && (
          <Key onTap={onEnter} className="flex-[2] !text-2xl font-semibold">
            {enterLabel}
          </Key>
        )}
      </div>
    </div>
  );
}
